import React, { useState, useEffect, useCallback, useRef } from 'react';
import { studentsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { FiSearch, FiRefreshCw, FiX, FiRotateCcw, FiAlertTriangle } from 'react-icons/fi';
import './Students.css';
import './StudentModal.css';
import './ClientVertical.css';

const STATUS_COLORS = {
  NEW: '#011ff3',
  CONTACTED: '#f5a623',
  INTERESTED: '#38f9d7',
  APPLIED: '#4facfe',
  ENROLLED: '#43e97b',
  REJECTED: '#f5576c',
  INACTIVE: '#94a3b8',
};

// ── Toast ─────────────────────────────────────────────────────────────────────
const Toast = ({ msg, type }) => (
  <div style={{
    position: 'fixed', top: 24, right: 24, zIndex: 3000,
    background: type === 'success' ? '#43e97b' : '#f5576c',
    color: '#fff', fontWeight: 700, borderRadius: 10,
    padding: '12px 22px', fontSize: 14,
    boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
  }}>
    {msg}
  </div>
);

// ── Restore confirmation modal ────────────────────────────────────────────────
const RestoreModal = ({ student, onConfirm, onCancel, loading }) => (
  <div className="modal-overlay">
    <div className="modal-content" style={{ maxWidth: 420, textAlign: 'center', padding: 32 }}>
      <div style={{
        width: 52, height: 52, borderRadius: '50%',
        background: 'rgba(67,233,123,0.12)',
        border: '2px solid #43e97b',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 20px',
      }}>
        <FiRotateCcw size={22} color="#43e97b" />
      </div>
      <h3 style={{ color: '#1e293b', margin: '0 0 8px', fontSize: 18 }}>Re-activate student?</h3>
      <p style={{ color: '#475569', margin: '0 0 4px', fontSize: 14 }}>
        <strong>{student.full_name || `${student.first_name} ${student.last_name}`.trim() || '—'}</strong>
      </p>
      <p style={{ color: '#94a3b8', fontSize: 12, margin: '0 0 28px' }}>
        {student.email} · {student.student_id}
      </p>
      <p style={{ color: '#475569', fontSize: 13, margin: '0 0 28px', lineHeight: 1.6 }}>
        This restores the record and makes it visible in the main students list.
        Previous status <strong>({student.status})</strong> will be retained.
      </p>
      <div style={{ display: 'flex', gap: 12 }}>
        <button className="btn-secondary" onClick={onCancel} disabled={loading} style={{ flex: 1 }}>Cancel</button>
        <button
          className="btn-primary"
          onClick={onConfirm}
          disabled={loading}
          style={{ flex: 1, background: '#43e97b', borderColor: '#43e97b' }}
        >
          {loading ? 'Restoring…' : 'Yes, Re-activate'}
        </button>
      </div>
    </div>
  </div>
);

// ── Student detail modal ──────────────────────────────────────────────────────
const StudentDetailModal = ({ student, onClose, onRestore, canRestore }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState(null);

  const handleRestore = async () => {
    setRestoring(true);
    setError(null);
    try {
      await onRestore(student.id);
      // onRestore handles close + toast
    } catch (e) {
      setError(
        e?.response?.data?.error ||
        e?.response?.data?.detail ||
        'Failed to restore student. Please try again.'
      );
      setRestoring(false);
      setConfirmOpen(false);
    }
  };

  const Field = ({ label, value }) => {
    if (!value && value !== 0) return null;
    return (
      <div style={{ marginBottom: 14 }}>
        <div style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 3 }}>
          {label}
        </div>
        <div style={{ color: '#334155', fontSize: 14 }}>{value}</div>
      </div>
    );
  };

  const deletedAt  = student.deleted_at  ? new Date(student.deleted_at).toLocaleString()  : 'Unknown';
  const createdAt  = student.created_at  ? new Date(student.created_at).toLocaleDateString() : '—';
  const statusColor = STATUS_COLORS[student.status] || '#94a3b8';

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" style={{ width: 560, padding: 28 }} onClick={e => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{
                width: 48, height: 48, borderRadius: '50%',
                background: 'rgba(245,87,108,0.1)', border: '2px solid rgba(245,87,108,0.35)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 20, color: '#f5576c', fontWeight: 700,
              }}>
                {(student.first_name?.[0] || '?').toUpperCase()}
              </div>
              <div>
                <h2 style={{ color: '#1e293b', margin: 0, fontSize: 18 }}>
                  {student.full_name || `${student.first_name} ${student.last_name}`.trim() || '—'}
                </h2>
                <span className="status-pill" style={{ color: '#f5576c', marginTop: 4, display: 'inline-block' }}>DELETED</span>
              </div>
            </div>
            <button onClick={onClose} className="modal-close"><FiX size={20} /></button>
          </div>

          <div className="error-message" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
            <FiAlertTriangle color="#f5576c" size={16} />
            <div>
              <div style={{ color: '#f5576c', fontSize: 12, fontWeight: 600 }}>Deleted record</div>
              <div style={{ color: '#666', fontSize: 12 }}>Removed on {deletedAt}</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
            <Field label="Student ID" value={student.student_id} />
            <Field label="Status at deletion" value={
              <span className="status-pill" style={{ color: statusColor }}>{student.status}</span>
            } />
            <Field label="Email" value={student.email} />
            <Field label="Phone" value={student.phone || '—'} />
            <Field label="Home country" value={student.country || '—'} />
            <Field label="Destination" value={student.destination_country || '—'} />
            <Field label="University" value={student.university || '—'} />
            <Field label="Assigned agent" value={student.assigned_agent || '—'} />
            <Field label="Created" value={createdAt} />
          </div>

          {student.notes && (
            <div style={{ marginTop: 16 }}>
              <div style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>Notes</div>
              <div style={{
                background: '#f8fafc', border: '1px solid #e0e0e0', borderRadius: 8,
                padding: '10px 14px', color: '#475569', fontSize: 13, lineHeight: 1.6,
                whiteSpace: 'pre-wrap', maxHeight: 140, overflowY: 'auto',
              }}>
                {student.notes}
              </div>
            </div>
          )}

          {error && <div className="error-message" style={{ marginTop: 16 }}>{error}</div>}

          <div style={{ marginTop: 28, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button onClick={onClose} className="btn-secondary" style={{ minWidth: 'auto' }}>Close</button>
            {canRestore && (
              <button
                onClick={() => setConfirmOpen(true)}
                disabled={restoring}
                className="btn-primary"
                style={{ background: '#43e97b', borderColor: '#43e97b', minWidth: 'auto', display: 'flex', alignItems: 'center', gap: 7 }}
              >
                <FiRotateCcw size={15} />
                {restoring ? 'Restoring…' : 'Re-activate'}
              </button>
            )}
          </div>
        </div>
      </div>

      {confirmOpen && (
        <RestoreModal
          student={student}
          onConfirm={handleRestore}
          onCancel={() => setConfirmOpen(false)}
          loading={restoring}
        />
      )}
    </>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const DeletedStudents = () => {
  const { isAdmin } = useAuth();

  const [students, setStudents]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage]             = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]           = useState(0);
  const [selected, setSelected]     = useState(null);
  const [toast, setToast]           = useState(null);
  const toastTimer                  = useRef(null);

  const showToast = useCallback((msg, type = 'success') => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ msg, type });
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  }, []);

  const load = useCallback(async (targetPage = page, q = appliedSearch) => {
    setLoading(true);
    setError(null);
    try {
      const res = await studentsAPI.listDeleted({ q, page: targetPage });
      const data = res.data;
      const safePage = Math.min(targetPage, data.total_pages || 1);
      // If the requested page no longer exists after removal, reload page 1
      if (safePage < targetPage) {
        return load(safePage, q);
      }
      setStudents(data.results || []);
      setTotalPages(data.total_pages || 1);
      setTotal(data.count || 0);
      setPage(targetPage);
    } catch (e) {
      setError(
        e?.response?.data?.error ||
        e?.response?.data?.detail ||
        'Failed to load deleted students.'
      );
    } finally {
      setLoading(false);
    }
  }, [page, appliedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initial load + reload when search or page changes
  useEffect(() => {
    load(page, appliedSearch);
  }, [page, appliedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setAppliedSearch(searchInput.trim());
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setPage(1);
    setAppliedSearch('');
  };

  const handleRestore = async (id) => {
    await studentsAPI.restore(id);
    const newTotal = total - 1;
    const newTotalPages = Math.max(1, Math.ceil(newTotal / 30));
    const newPage = page > newTotalPages ? newTotalPages : page;
    setSelected(null);
    showToast('Student re-activated successfully.');
    // Reload the correct page
    setTotal(newTotal);
    load(newPage, appliedSearch);
  };

  const canRestore = isAdmin();

  return (
    <div className="students-page">
      {toast && <Toast msg={toast.msg} type={toast.type} />}

      <div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1e293b' }}>Deleted Students</h1>
        <p style={{ margin: '6px 0 0', color: '#94a3b8', fontSize: 14 }}>
          Soft-deleted records — <strong style={{ color: '#64748b' }}>{total}</strong> total
          {!canRestore && (
            <span style={{ color: '#f5a623', marginLeft: 8 }}>
              · Only admins (Admin / CEO / COO) can re-activate records
            </span>
          )}
        </p>
      </div>

      <form onSubmit={handleSearch} className="header-actions-right" style={{ maxWidth: 560 }}>
        <div className="search-box" style={{ flex: 1 }}>
          <FiSearch />
          <input
            type="text"
            placeholder="Search by name, email, or ID…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
        </div>
        <button type="submit" className="btn-secondary" style={{ minWidth: 'auto' }}>Search</button>
        <button type="button" onClick={() => load(page, appliedSearch)} title="Refresh" className="btn-icon">
          <FiRefreshCw size={15} className={loading ? 'cv-spin' : ''} />
        </button>
      </form>

      {loading ? (
        <div className="empty-state">Loading…</div>
      ) : error ? (
        <div className="error-message" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <FiAlertTriangle size={18} />
          <span>{error}</span>
          <button onClick={() => load(page, appliedSearch)} style={{ marginLeft: 'auto', color: '#c33', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}>
            Retry
          </button>
        </div>
      ) : students.length === 0 ? (
        <div className="empty-state">
          {appliedSearch ? `No deleted students match "${appliedSearch}".` : 'No deleted students found.'}
          {appliedSearch && (
            <div>
              <button onClick={handleClearSearch} style={{ marginTop: 12, color: '#011ff3', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}>
                Clear search
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="students-table-container">
            <table className="students-table">
              <thead>
                <tr>{['Student', 'ID', 'Email', 'Status', 'Agent', 'Deleted On'].map(h => <th key={h}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {students.map((s) => {
                  const statusColor = STATUS_COLORS[s.status] || '#94a3b8';
                  const name = s.full_name || `${s.first_name} ${s.last_name}`.trim() || '—';
                  return (
                    <tr key={s.id} onClick={() => setSelected(s)} style={{ cursor: 'pointer' }}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{
                            width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                            background: 'rgba(245,87,108,0.08)',
                            border: '1.5px solid rgba(245,87,108,0.25)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#f5576c', fontWeight: 700, fontSize: 13,
                          }}>
                            {(s.first_name?.[0] || '?').toUpperCase()}
                          </div>
                          <strong>{name}</strong>
                        </div>
                      </td>
                      <td className="cv-id">{s.student_id}</td>
                      <td>{s.email || '—'}</td>
                      <td><span className="status-pill" style={{ color: statusColor }}>{s.status}</span></td>
                      <td style={{ color: '#94a3b8' }}>{s.assigned_agent || '—'}</td>
                      <td style={{ color: '#94a3b8', fontSize: 12 }}>
                        {s.deleted_at ? new Date(s.deleted_at).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12 }}>
              <button className="btn-icon" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>← Prev</button>
              <span style={{ color: '#64748b', fontSize: 13 }}>
                Page <strong>{page}</strong> of {totalPages}
                <span style={{ color: '#94a3b8', marginLeft: 8 }}>({total} records)</span>
              </span>
              <button className="btn-icon" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next →</button>
            </div>
          )}
        </>
      )}

      {selected && (
        <StudentDetailModal
          student={selected}
          onClose={() => setSelected(null)}
          onRestore={handleRestore}
          canRestore={canRestore}
        />
      )}
    </div>
  );
};

export default DeletedStudents;
